import './App.css';
import ReactResizeDetector from 'react-resize-detector';
import {useEffect, useRef, useState} from "react";
import styled, {createGlobalStyle, css} from 'styled-components'
import { useTranslation } from "react-i18next";
import i18n from "./i18n"

const GlobalStyles = createGlobalStyle`
  // TODO get this to be configurable
  .styled-heading {
    max-width: 760px;
    margin: 0 auto;
    margin-bottom: 4rem;

    p {
      text-align: center;
    }
  }

  sup {
    font-size: 12px;
  }
`

const StyledCalculator = styled.form`
  * {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif;
    color: #262626;
  }

  width: 100%;
  max-width: 1000px;

  a {
    color: rgb(92, 107, 57);
  }

  p {
    line-height: 140%;
  }
`

const StyledHeading = styled.div`
  text-align: center;

  .heading {
    font-size: 30px;
    font-weight: bold;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border-bottom: ${({isRetailer}) => isRetailer ? 'none' : '3px solid rgb(92, 107, 57)'};
    padding-bottom: ${({isRetailer}) => isRetailer ? '0' : '40px'};
    margin-bottom: ${({isRetailer}) => isRetailer ? '1rem' : '50px'};

    @media (min-width: 500px) {
      font-size: 36px;
      flex-direction: row;
      justify-content: ${({isRetailer}) => isRetailer ? 'flex-start' : 'center'};
    }
  }

  span {
    display: block;
    order: 2;
  }

  .logo {
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 1rem;
    order: 1;
  }

  @media (min-width: 500px) {
    span {
      margin-right: ${({isRetailer}) => isRetailer ? '2rem' : '0'};
      order: 1;
    }

    .logo {
      order: 2;
      margin-right: 0;
      margin-top: 8px;
      margin-bottom: 0;
    }
  }

  p {
    margin-bottom: 32px;
  }
`

const StyledUnits = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  margin-bottom: 40px;
`

const StyledUnitSelector = styled.div`
  border: 1px solid #BEBEBE;
  color: #717171;
  cursor: pointer;
  padding: 8px 16px;
  background: #fff;
  font-weight: bold;

  ${({selected}) => selected && css`
    background-color: #F2F3EC;
    border: 1px solid #7E8C4D;
    color: #7E8C4D;
  `}
`

const StyledInput = styled.input`
  position: relative;
  border: none;
  border-bottom: 2px solid #000;
  font-size: 24px;
  text-align: center;
  width: 100%;

  &:focus {
    border-bottom: 2px solid #798C4B;
    outline: none;
  }


`

const StyledFormGroup = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin-bottom: 48px;

  label {
    font-size: 20px;
    color: #262626;
    font-weight: bold;
    margin-right: 16px;
    min-width: 135px;
  }

  &:after {
    content: '${({unit}) => unit}';
    position: absolute;
    right: 30px;
    color: #262626;
    font-size: 18px;
  }
`

const StyledContent = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr 1px 1fr;
  grid-gap: 60px;

  @media (max-width: 768px) {
    grid-gap: 30px;
    grid-template-columns: 2.5fr 1px 3fr;
  }

  .divider {
    background-color: #C8C8C8;
    height: 100%;
  }

  @media (max-width: 650px) {
    grid-gap: 0;
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;

    .divider {
      margin-bottom: 8px;
      height: 1px;
    }
  }
`

const StyledGreyText = styled.div`
  color: #717171;
  line-height: 34px;
`

const StyledResults = styled.div`
  .total {
    font-weight: 900;
    font-size: 36px;
    line-height: 48px;
  }

  .subtotal {
    font-size: 20px;
    display: flex;
    align-items: center;

    .litres {
      margin-left: auto;

    }
  }

  .recommend {
    margin-top: 24px;
    display: flex;
    align-items: center;
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
  }

  .product-info {
    margin-left: 32px;
    @media (max-width: 768px) {
      margin-left: 0;
    }
  }

  hr {
    margin: 8px 0;
    background-color: #C8C8C8;
    height: 1px;
    width: 100%;
    border: none;
  }
`

const StyledSelect = styled.div`
  margin-bottom: 48px;

  span {
    display: block;
    font-weight: bold;
    margin-bottom: 8px;
  }

  select {
    font-size: 16px;
    padding: 10px 16px;
    border-radius: 5px;
    width: 100%;
  }
`

const StyledScrollMessage = styled.div`
  display: none;
  position: sticky;
  bottom: 40px;
  font-size: 16px;
  line-height: 100%;
  padding: 12px;
  background-color: #7E8B51;
  text-align: center;
  color: #fff;
  margin-bottom: 32px;

  @media (max-width: 650px) {
    display: block;
  }
`

const conversion = {
    // all input units are in ml
    imperial: {
        name: 'Imperial',
        toMl: (volume) => volume * 16.387,
        unitSuffix: 'in',
        convert: (ml) => {
            const floz = ml * 0.033814

            if (floz < 128) {
                return {unit: 'fl oz', amount: floz}
            } else {
                return {unit: 'gal', amount: ml * 0.000264172}
            }
        }
    },

    metric: {
        name: 'Metric',
        toMl: (volume) => volume,
        unitSuffix: 'cm',
        convert: (ml) => {
            if (ml < 1000) {
                return {unit: 'mL', amount: ml}
            } else {
                return {unit: 'L', amount: ml / 1000}
            }
        }
    },
}

const products = {
    flowcast: [
        {
            min: 0,
            max: 6000,
            name: '6 L Kit',
            code: 'EPFLK10-6L',
            image: 'EPFLK10-6L.png',
        },
        {
            min: 6000,
            max: 12000,
            name: '12 L Kit',
            code: 'EPFLK10-12L',
            image: 'EPFLK10-12L.png',
        },
        {
            min: 12000,
            max: 30000,
            name: '30 L Kit',
            code: 'EPFLK10-30L',
            image: 'EPFLK10-30L.png',
        },
        {
            min: 30000,
            max: 60000,
            name: '60 L Kit',
            code: 'EPFLK10-60L',
            image: 'EPFLK10-60L.png',
        },
        {
            min: 60000,
            max: 600000,
            name: '600 L Drum Set',
            code: 'EPFLK10-600L',
            image: 'EPFLK10-600L.png',
        },
        {
            min: 600000,
            max: 2765000,
            name: '2949 kg (2765L) Tote Set',
            code: 'EPFLK10-2949KG',
            image: 'EPFLK10-600L.png',
        },
    ],

    flowcastSpr: [
        {
            min: 0,
            max: 750,
            name: '750 mL Kit',
            code: 'EPSPK10-750ML',
            image: 'EPSPK10-750ML.jpg',
        },
        {
            min: 750,
            max: 1500,
            name: '1.5 L Kit',
            code: 'EPSPK10-1.5L',
            image: 'EPSPK10-1-5L.jpg',
        },
        {
            min: 1500,
            max: 3000,
            name: '3 L Kit',
            code: 'EPSPK10-3L',
            image: 'EPSPK10-3L.jpg',
        },
        {
            min: 3000,
            max: 6000,
            name: '6 L Kit',
            code: 'EPSPK10-6L',
            image: 'EPSPK10-6L.jpg',
        },
        {
            min: 6000,
            max: 12000,
            name: '12 L Kit',
            code: 'EPSPK10-12L',
            image: 'EPSPK10-12L.jpg',
        },
        {
            min: 12000,
            max: 30000,
            name: '30 L Kit',
            code: 'EPSPK10-30L',
            image: 'EPSPK10-30L.jpg',
        },
    ],

    snowWhite: [
        {
            min: 0,
            max: 15000,
            name: '15 L Kit',
            code: 'EPSWK20-15L',
            image: 'EPSWK20-15L.jpg',
        },
    ],

    uvpoxy: [
        {
            min: 0,
            max: 1000,
            name: '1 L UVPoxy Kit',
            code: 'EPUVK20-1L',
            image: 'EPUVK20-1L.jpg',
        },
        {
            min: 1000,
            max: 2000,
            name: '2 L UVPoxy Kit',
            code: 'EPUVK20-2L',
            image: 'EPUVK20-2L.jpg',
        },
        {
            min: 2000,
            max: 4000,
            name: '4 L UVPoxy Kit',
            code: 'EPUVK20-4L',
            image: 'EPUVK20-4L.jpg',
        },
        {
            min: 4000,
            max: 8000,
            name: '8 L UVPoxy Kit',
            code: 'EPUVK20-8L',
            image: 'EPUVK20-8L.jpg',
        },
        {
            min: 8000,
            max: 20000,
            name: '20 L UVPoxy Kit',
            code: 'EPUVK20-20L',
            image: 'EPUVK20-20L.jpg',
        },
        {
            min: 20000,
            max: 40000,
            name: '40 L UVPoxy Kit',
            code: 'EPUVK20-40L',
            image: 'EPUVK20-40L.jpg',
        },
        {
            min: 40000,
            max: 342000,
            name: '362kg (342 L) UVPoxy Drum Set',
            code: 'EPUVK20-362KG',
            image: 'EPUVK20-362KG.jpg',
        },
        {
            min: 342000,
            max: 1736000,
            name: '1826kg (1736L) UVPoxy Tote Set',
            code: 'EPUVK20-1826KG',
            image: 'EPUVK20-1826KG.jpg',
        },
    ]
}

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
function debounce(func, wait, immediate) {
    var timeout;
    return function () {
        var context = this, args = arguments;
        var later = function () {
            timeout = null;
            if (!immediate) {
                func.apply(context, args);
            }
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) {
            func.apply(context, args);
        }
    };
}

const fireDataLayer = debounce(function (product, ml) {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
        "event": "volumeCalculated",
        "product": {
            name: product.name,
            code: product.code,
        },
        "volumeMl": ml,
        "isRetailer": window.calcOptions && window.calcOptions.isRetailer,
    })
}, 3000);

function getRecommendedProduct(product, ml) {
    if (!ml) {
        return null;
    }

    const recommended = product.filter(({min, max}) => ml >= min && ml <= max)

    if (!recommended.length) {
        // We dont have a kit their size. so they need multiple of the biggest kits
        const sorted = product.sort((first, second) => first.max < second.max ? -1 : 1)

        fireDataLayer(sorted[sorted.length - 1], ml)

        return sorted[sorted.length - 1];
    }

    fireDataLayer(recommended[0], ml)

    return recommended[0];
}

function App() {
    const [selectedProduct, setSelectedProduct] = useState('flowcast');
    const [dimensions, setDimensions] = useState({width: '', length: '', height: ''});
    const [inputUnit, setInputUnit] = useState('metric');
    const resultsRef = useRef();
    const { t } = useTranslation();

    const lang = window?.calcOptions?.lang ?? 'en';
    console.log(lang)

    useEffect(() => {
        setDimensions({width: '', length: '', height: ''})
    }, [inputUnit])

    useEffect(() => {
        i18n.changeLanguage(lang);
    }, [lang])

    const isRetailer = window.calcOptions && window.calcOptions.isRetailer;

    const {toMl, unitSuffix, convert} = conversion[inputUnit];

    function notifyParent(height) {
        window.parent.postMessage({
            frameHeight: height,
        }, '*');
    }

    const calculateVolume = (unit = 'metric', dimensions = {}) => {
        const {width = 0, length = 0, height = 0} = dimensions;
        const volume = (width || 0) * (length || 0) * (height || 0);
        const ml = toMl(volume)
        const converted = convert(ml)

        const mlOversized = Math.round(ml * 105 + Number.EPSILON) / 100;

        // Add 5% for margin of error and round our number to 2 decimal
        const otherUnit = Object.keys(conversion).find((u) => u !== unit)
        const alternateUnit = conversion[otherUnit]['convert'](ml);

        return {
            ...converted,
            amount: addPercentageAndRound(converted['amount']),
            original: round(converted['amount']),
            ml: mlOversized,
            alternateUnit: {...alternateUnit, amount: addPercentageAndRound(alternateUnit['amount'])},
            product: getRecommendedProduct(products[selectedProduct], mlOversized)
        }
    }

    const addPercentageAndRound = (amount, percentage = 1.05) => {
        return round(amount * percentage, 2);
    }

    const round = (value, decimals = 2) => {
        return Math.round((value * Math.pow(10, decimals)) + Number.EPSILON) / Math.pow(10, decimals);
    }

    const {unit, amount, product, ml, alternateUnit} = calculateVolume(inputUnit, dimensions);

    return (
        <ReactResizeDetector
            handleWidth
            handleHeight
            onResize={(width, height) => notifyParent(height)}
        >
            {({width, height}) =>
                <StyledCalculator>
                    <GlobalStyles/>
                    <StyledHeading isRetailer={isRetailer} className={'styled-heading'}>
                        <div className={'heading'}>
                            <span>EcoPoxy<sup>®</sup> {t('Volume Calculator')}</span>
                            {isRetailer &&
                                <a className={'logo'} href={'https://www.ecopoxy.com/'} rel={'noreferrer noopener'} target={'_blank'}>
                                    <img alt={'ecopoxy logo'} width="220px" src={process.env.PUBLIC_URL + '/images/ecopoxy-logo.svg'}/>
                                </a>
                            }
                        </div>
                        <p>{t('The volume calculator can be used to determine the amount of epoxy required for your project. We suggest using an average for each measurement. Please refer to the diagram below if unsure how to measure the area for your pour.')}</p>
                    </StyledHeading>
                    <StyledContent>
                        <div className={'col'}>
                            <StyledUnits>
                                <span style={{marginLeft: 'auto', marginRight: 8}}>{t('Unit of measure')}</span>
                                <StyledUnitSelector onClick={() => setInputUnit('imperial')} selected={inputUnit === 'imperial'}>{t('IMPERIAL')}</StyledUnitSelector>
                                <StyledUnitSelector onClick={() => setInputUnit('metric')} selected={inputUnit === 'metric'}>{t('METRIC')}</StyledUnitSelector>
                            </StyledUnits>
                            <StyledSelect>
                                <label><span>{t('Select a product')}:</span>
                                    <select defaultValue={'flowcast'} onChange={(e) => setSelectedProduct(e.target.value)}>
                                        <option value={'flowcast'}>{t('FlowCast®️ - 13-38 mm (0.5-1.5") per layer')}</option>
                                        <option value={'flowcastSpr'}>{t('FlowCast®️ SPR - 6-25 mm (0.25-1") per layer')}</option>
                                        <option value={'uvpoxy'}>{t('UVPoxy®️ - Up to 3.2mm (1/8") per coat')}</option>
                                        <option value={'snowWhite'}>{t('SnowWhite - 13-38 mm (0.5-1.5") per layer')}</option>
                                    </select>
                                </label>
                            </StyledSelect>
                            <StyledFormGroup unit={t(unitSuffix)}>
                                <label>{t('Width')}</label>
                                <StyledInput
                                    type={'number'}
                                    value={dimensions.width || ''}
                                    onChange={(e) => setDimensions({
                                        ...dimensions,
                                        width: parseFloat(e.target.value)
                                    })}/>
                            </StyledFormGroup>

                            <StyledFormGroup unit={t(unitSuffix)}>
                                <label>{t('Length')}</label>
                                <StyledInput
                                    type={'number'}
                                    value={dimensions.length || ''}
                                    onChange={(e) => setDimensions({
                                        ...dimensions,
                                        length: parseFloat(e.target.value)
                                    })}/>
                            </StyledFormGroup>

                            <StyledFormGroup unit={t(unitSuffix)}>
                                <label>{selectedProduct === 'uvpoxy' ? t('Thickness') : t('Depth')}</label>
                                <StyledInput
                                    type={'number'}
                                    value={dimensions.height || ''}
                                    onChange={(e) => {
                                        setDimensions({
                                            ...dimensions,
                                            height: parseFloat(e.target.value)
                                        });
                                    }}/>
                            </StyledFormGroup>

                            {!!amount &&
                                <StyledScrollMessage onClick={() => resultsRef.current.scrollIntoView({behavior: "smooth"})}>
                                    &#x2193; {t('Scroll down to see results')}
                                </StyledScrollMessage>
                            }
                        </div>

                        <div className={'divider'}></div>

                        <div className={'col'}>
                            {!amount && selectedProduct !== 'uvpoxy' &&
                                <div>
                                    <p><strong>{t('How to Measure the Average Pour Width of a Casting Project')}</strong></p>
                                    <img width="100%" src={process.env.PUBLIC_URL + `/images/pour-measurement-${lang}.jpg`} alt={"how to take measurements"}/>
                                </div>
                            }

                            {!amount && selectedProduct === 'uvpoxy' &&
                                <div>
                                    <p><strong>{t('How to Measure A Surface for a Coating Project')}</strong></p>
                                    <img width="100%" src={process.env.PUBLIC_URL + `/images/pour-measurement-coating-${lang}.jpg`} alt={"how to take measurements"}/>
                                </div>
                            }

                            {!!amount &&
                                <StyledResults ref={resultsRef}>
                                    <div>
                                        <StyledGreyText>{t('You will need about:')}</StyledGreyText>
                                        <div style={{display: 'flex', alignItems: 'center'}}>
                                            <div className={'total'}> {amount} {t(unit)}*</div>
                                            <StyledGreyText style={{marginLeft: 'auto'}}>({alternateUnit.amount} {t(alternateUnit.unit)})</StyledGreyText>
                                        </div>
                                    </div>
                                    <hr/>
                                    <div className={'recommend'}>
                                        <img src={process.env.PUBLIC_URL + '/images/' + product.image} alt={selectedProduct} width={121}/>
                                        <div className={'product-info'}>
                                            <StyledGreyText style={{}}>{t('We recommend')}{product.max < ml ? t(' multiple kits of') : ''}:</StyledGreyText>
                                            <div style={{fontSize: 30, fontWeight: 'bold'}}>{t(product.name)}</div>
                                            <div style={{
                                                fontSize: 20,
                                                fontWeight: 500,
                                                lineHeight: '170%'
                                            }}>{t('Product #:')} {t(product.code)}</div>
                                            {!isRetailer &&
                                                <a href={'https://www.ecopoxy.com/retail-locator/'} rel="noreferrer" target={'_blank'}>{t('Find Closest Retailer')}</a>
                                            }
                                        </div>
                                    </div>
                                    <hr style={{marginTop: 24, marginBottom: 24}}/>
                                    <div style={{
                                        fontSize: 12,
                                        display: selectedProduct === 'flowcast' ? 'block' : 'none'
                                    }}>
                                        <p>
                                            <strong>{t('Note:')}</strong> {t('FlowCast® has been formulated with a simple 2:1 mix ratio and is designed for larger projects such as river tables, ocean tables, and encapsulated objects. It can be combined with Liquid or Metallic pigments to create custom colors and effects. FlowCast® is intended for pours of 13-38 mm (0.5-1.5") per layer, with a maximum pour volume of 30 L (without the use of additional cooling). FlowCast® can be worked using the same tools you have in your woodshop.')}
                                        </p>
                                        <p>
                                            <i>
                                                {t('* While FlowCast®️ will not shrink during curing under normal conditions, the FlowCast® Volume Calculator includes an additional 5% in the calculated volume required for the project. This extra 5% is to ensure the target pour depth is met by addressing resin that fills cracks and voids in the wood.')}
                                            </i>
                                        </p>
                                    </div>

                                    <div style={{
                                        fontSize: 12,
                                        display: selectedProduct === 'flowcastSpr' ? 'block' : 'none'
                                    }}>
                                        <p>
                                            <strong>{t('Note:')}</strong> {t('FlowCast® SPR has been formulated with a simple 2:1 mix ratio and is designed for small castings, allowing users to create beautiful live edge wood projects with thinner profiles such as coasters and serving trays. FlowCast® SPR is intended for pours from 6-25 mm (0.25-1") per layer, with a maximum volume of 3 L. Use FlowCast® SPR by itself for a water-clear finish or add our Liquid, Metallic, or Glitter pigments to create custom colors and effects.')}
                                        </p>
                                        <p>
                                            {t('During the pour it is best to prevent resin from flooding over top of wood slabs. Thin resin over top of the wood slabs will take longer to cure, resulting in extended wait times for finishing.')}
                                        </p>
                                        <p>
                                            <i>
                                                {t('* While FlowCast® SPR will not shrink during curing under normal conditions, the Volume Calculator includes an additional 5% in the calculated volume required for the project. This extra 5% is to ensure the target pour depth is met by addressing resin that fills cracks and voids in the wood.')}
                                            </i>
                                        </p>
                                    </div>

                                    <div style={{
                                        fontSize: 12,
                                        display: selectedProduct === 'snowWhite' ? 'block' : 'none'
                                    }}>
                                        <p>
                                            <strong>{t('Note:')}</strong> {t('SnowWhite uses a simple 2:1 mix ratio and is highly pigmented. It has been formulated for large casting applications, such as river tables or large furniture pieces. The high opacity, pure white color beautifully contrasts wood grain in live edge projects and allows the reclaimed wood to shine. SnowWhite is intended for pours of 13-38 mm (0.5-1.5") per layer, with a maximum pour volume of 16 L (without the use of additional cooling). SnowWhite can be worked using the same tools you have in your woodshop.')}
                                        </p>
                                        <p>
                                            <i>
                                                {t('* While SnowWhite will not shrink during curing under normal conditions, the Volume Calculator includes an additional 5% in the calculated volume required for the project. This extra 5% is to ensure the target pour depth is met by addressing resin that fills cracks and voids in the wood.')}
                                            </i>
                                        </p>
                                    </div>

                                    <div style={{
                                        fontSize: 12,
                                        display: selectedProduct === 'uvpoxy' ? 'block' : 'none'
                                    }}>
                                        <p>
                                            <strong>{t('Note:')}</strong> {t('UVPoxy®️ has been formulated with a simple 1:1 mix ratio and is designed for tabletops, wood finishing, and fluid art. It is a high-performance, two-component, water-clear epoxy system that resists yellowing caused by the sun and other UV sources. Use UVPoxy by itself for a water-clear finish or add our Liquid, Metallic, or Glitter pigments to create custom colors and effects. It is self-leveling and can quickly be built up in 3.2 mm (1/8") layers.')}
                                        </p>
                                        <p>
                                            <i>
                                                {t('* While UVPoxy® will not shrink during curing under normal conditions, the Volume Calculator includes an additional 5% in the calculated volume required for the project. This extra 5% is to ensure the target coating thickness is met by addressing resin that fills cracks and voids in the wood.')}
                                            </i>
                                        </p>
                                    </div>

                                </StyledResults>
                            }

                        </div>
                    </StyledContent>
                </StyledCalculator>
            }
        </ReactResizeDetector>
    );
}

export default App;
